import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  ArrowRight, 
  Clock, 
  MapPin, 
  Instagram, 
  Facebook, 
  X, 
  ChevronRight, 
  ChevronDown 
} from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const brandBlue = '#004e6e';
  const brandRed = '#ff4a4a';

  const navLinks = [
    { name: 'Home', id: 'home', num: '01' },
    { name: 'Über uns', id: 'about', num: '02' },
    { name: 'Leistungen', id: 'services', num: '03' },
    { name: 'Referenzen', id: 'testimonials', num: '04' },
    { name: 'FAQ', id: 'faq', num: '05' },
  ];

  // Secondary links for the grid section in the mobile menu
  const utilityLinks = [
    { name: 'Über uns', id: 'about' },
    { name: 'Support', id: 'contact' },
    { name: 'Referenzen', id: 'testimonials' },
    { name: 'Kontakt', id: 'contact' },
    { name: 'Sitemap', id: 'home' },
    { name: 'Cookie settings', id: 'faq' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
  }, [isOpen]);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      window.scrollTo({ top: section.offsetTop - 80, behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* ================= TOP BAR (NOT TOUCHED) ================= */}
      <div className={`bg-[#0f172a] text-white py-2 hidden md:block transition-all duration-300 ${scrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-auto opacity-100'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center text-[10px] font-black uppercase tracking-[0.2em]">
          <div className="flex gap-6">
            <span className="flex items-center gap-2"><Clock size={12} className="text-red-500"/> Mo-Fr 07:30-17:30</span>
            <span className="flex items-center gap-2"><MapPin size={12} className="text-red-500"/> Büren an der Aare</span>
          </div>
          <a href="tel:+41787096702" className="hover:text-red-500 transition-colors">+41 78 709 67 02</a>
        </div>
      </div>

      {/* ================= HEADER (DESKTOP NAV NOT TOUCHED) ================= */}
      <header className={`sticky top-0 z-[100] transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-6' : 'bg-white py-4'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          
          {/* LOGO */}
          <a href="/" className="flex items-center gap-2 h-[50px] w-[150px]">
            <img 
              src="/img/123.png" 
              alt="Logo" 
            />
          </a>

          {/* DESKTOP NAV (NOT TOUCHED) */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <button 
                key={link.id} 
                onClick={() => scrollToSection(link.id)}
                className="text-[12px] font-black uppercase tracking-widest text-slate-700 hover:text-red-500 transition-colors relative group"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-red-500 transition-all group-hover:w-full" />
              </button>
            ))}
            <button 
              onClick={() => scrollToSection('contact')}
              className="px-6 py-2.5 bg-[#004e6e] text-white rounded-full font-black text-[11px] uppercase tracking-widest hover:bg-red-500 transition-all shadow-md"
            >
              Angebot
            </button>
          </nav>

          {/* REACT HAMBURGER BUTTON (TRIGGER) */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden relative z-[1001] w-12 h-12 flex flex-col items-center justify-center gap-1.5 focus:outline-none bg-slate-100 rounded-full active:scale-90 transition-transform"
            aria-label="Toggle Menu"
          >
            <span className={`w-6 h-0.5 bg-slate-900 rounded-full transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`w-6 h-0.5 bg-slate-900 rounded-full transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`} />
            <span className={`w-6 h-0.5 bg-slate-900 rounded-full transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </header>

      {/* MOBILE DRAWER OVERLAY */}
      <div 
        className={`fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[999] lg:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        onClick={() => setIsOpen(false)}
      />

      {/* ================= MOBILE SIDE DRAWER (UNTITLED UI STYLE) ================= */}
      <div
        className={`fixed top-0 right-0 h-full w-[95%] max-w-[400px] bg-white z-[1000] lg:hidden shadow-2xl transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* 1. Drawer Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 text-[#004e6e]">
            <div className="w-8 h-8 rounded-lg bg-[#004e6e] flex items-center justify-center p-1.5">
               <img src="/img/white.png" alt="SaniStyle" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-gray-900 tracking-tight text-lg">SaniStyle UI</span>
          </div>
          <button 
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
          >
            <X size={24} />
          </button>
        </div>

        {/* 2. Scrollable Body Area */}
        <div className="flex-grow overflow-y-auto">
          {/* Primary Accordion-style Links */}
          <nav className="flex flex-col px-2 pt-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="flex items-center justify-between w-full px-4 py-4 text-left font-semibold text-gray-700 hover:bg-gray-50 rounded-xl transition-colors group"
              >
                <span className="group-hover:text-red-500 transition-colors">{link.name}</span>
                <ChevronDown size={18} className="text-gray-400 :text-red-500" />
              </button>
            ))}
          </nav>

          <div className="h-px bg-gray-100 mx-6 my-4" />

          {/* Secondary Utility Link Grid */}
      
        </div>

        {/* 3. Bottom Action Footer (Stacked Buttons) */}
        <div className="p-6 border-t border-gray-100 bg-gray-50/50 space-y-4">
          <div className="space-y-3">
            <button 
              onClick={() => scrollToSection('contact')}
              className="w-full py-3.5 bg-red-500 text-white rounded-xl font-bold text-base shadow-lg shadow-red-500/20 active:scale-[0.98] transition-all"
            >
              Angebot anfordern
            </button>
            <a 
              href="tel:+41787096702" 
              className="w-full py-3.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold text-base flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Phone size={18} className="text-[#004e6e]" />
              Jetzt anrufen
            </a>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex gap-5">
              <Instagram size={20} className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors" />
              <Facebook size={20} className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors" />
            </div>
            <span className="text-xs text-gray-400 font-medium italic">© SaniStyle GmbH</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;